import type { FavoriteViewPayload } from "@/features/favoriteViews/types";

/**
 * A SHARE LINK's snapshot — everything that makes up "the exact view on my
 * screen": the saved-view payload (filters, search, view mode, Show/hide
 * columns) plus the List view's arrangement, column filters/sort and widths,
 * and whether the list was in full screen. Stored verbatim on the server
 * (`SharedListView.payload`) and re-applied to a `shared-<token>` route.
 */
export interface ListShareSnapshot {
  version: 1;
  source: {
    kind: "leads" | "stage" | "view";
    routeId: string | number;
    /** Stage routes: the pinned stage, so the shared list stays stage-scoped. */
    stageId?: number;
    path: string;
  };
  view: FavoriteViewPayload;
  list: {
    order?: string[];
    filters?: Record<string, string[]>;
    sort?: { key: string; dir: "asc" | "desc" } | null;
    widths?: Record<string, number>;
  };
  fullscreen: boolean;
}

export interface SharedListView {
  id: number;
  token: string;
  label: string;
  kind: "leads" | "stage" | "view";
  payload: ListShareSnapshot;
  createdAt: string;
  createdBy: { id: number; name: string } | null;
}

/** A Xale user this link has been handed to (server: SharedListViewRecipient). */
export interface SharedViewRecipient {
  id: number;
  createdAt: string;
  /** null = the email hop failed / SMTP isn't configured; the inbox card still landed. */
  emailSentAt: string | null;
  user: { id: number; name: string; email: string; avatar: string | null };
  sharedBy: { id: number; name: string } | null;
}

/** What POST /core/shared-views/:token/share answers with. */
export interface ShareResult {
  url: string;
  shared: { id: number; name: string; email: string; emailSent: boolean }[];
  /** Addresses that got nothing, each with the reason to show the admin. */
  skipped: { email: string; reason: string }[];
}

export const sharedRouteId = (token: string) => `shared-${token}`;

export function parseSharedRouteId(routeId: unknown): string | null {
  const m = /^shared-([A-Za-z0-9_-]{16,64})$/.exec(String(routeId ?? ""));
  return m ? m[1] : null;
}

export const sharedViewPath = (token: string) => `/shared/${token}`;
